<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="6" rounded="xxl" class="form-card">
            <!-- Page Header -->
            <v-card-title class="page-header py-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Add New Doctor</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ getCurrentStepDescription() }}
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Progress and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip color="info" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-doctor</v-icon>
                      Step {{ currentStep }} of {{ totalSteps }}
                    </v-chip>
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="handleBackNavigation"
                      class="back-button"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      {{ currentStep === 1 ? "Back to Doctors" : "Previous" }}
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Stepper -->
            <v-card-text class="px-4 py-3">
              <v-stepper v-model="currentStep" alt-labels class="elevation-0">
                <v-stepper-header>
                  <v-stepper-step
                    :complete="currentStep > 1"
                    step="1"
                    :color="
                      currentStep === 1
                        ? 'primary'
                        : currentStep > 1
                        ? 'success'
                        : 'grey'
                    "
                  >
                    Basic Details
                    <small>User Information</small>
                  </v-stepper-step>

                  <v-divider></v-divider>

                  <v-stepper-step
                    :complete="currentStep > 2"
                    step="2"
                    :color="
                      currentStep === 2
                        ? 'primary'
                        : currentStep > 2
                        ? 'success'
                        : 'grey'
                    "
                  >
                    Education
                    <small>Qualifications</small>
                  </v-stepper-step>

                  <v-divider></v-divider>

                  <v-stepper-step
                    :complete="currentStep > 3"
                    step="3"
                    :color="
                      currentStep === 3
                        ? 'primary'
                        : currentStep > 3
                        ? 'success'
                        : 'grey'
                    "
                  >
                    Registration
                    <small>Council Details</small>
                  </v-stepper-step>

                  <v-divider></v-divider>

                  <v-stepper-step
                    :complete="currentStep > 4"
                    step="4"
                    :color="
                      currentStep === 4
                        ? 'primary'
                        : currentStep > 4
                        ? 'success'
                        : 'grey'
                    "
                  >
                    Specializations
                    <small>Medical Areas</small>
                  </v-stepper-step>

                  <v-divider></v-divider>

                  <!-- Documents step hidden for now -->
                  <!-- <v-stepper-step 
                    :complete="currentStep > 5" 
                    step="5"
                    :color="currentStep === 5 ? 'primary' : (currentStep > 5 ? 'success' : 'grey')"
                  >
                    Documents
                    <small>File Uploads</small>
                  </v-stepper-step>

                  <v-divider></v-divider> -->

                  <v-stepper-step
                    :complete="currentStep > 5"
                    step="5"
                    :color="currentStep === 5 ? 'primary' : 'grey'"
                  >
                    Bank Details
                    <small>Payment Info</small>
                  </v-stepper-step>
                </v-stepper-header>

                <v-stepper-items>
                  <!-- Step 1: Basic Details -->
                  <v-stepper-content step="1">
                    <v-form
                      ref="basicForm"
                      v-model="basicFormValid"
                      lazy-validation
                    >
                      <div class="form-section">
                        <h3 class="section-title mb-4">
                          <v-icon color="primary" class="mr-2"
                            >mdi-account</v-icon
                          >
                          Basic Information
                        </h3>

                        <v-row>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="basicForm.first_name"
                              label="First Name *"
                              placeholder="Enter first name"
                              outlined
                              @keypress="onlyLetters"
                              :rules="[
                                (v) => !!v || 'First name is required',
                                (v) =>
                                  (v && v.length >= 2) ||
                                  'First name must be at least 2 characters',
                                (v) =>
                                  (v && v.length <= 50) ||
                                  'First name must be less than 50 characters',
                                (v) =>
                                  (v && /^[a-zA-Z\s]+$/.test(v)) ||
                                  'First name can only contain letters and spaces',
                              ]"
                              :error-messages="errors.first_name"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="basicForm.middle_name"
                              label="Middle Name (Optional)"
                              placeholder="Enter middle name"
                              outlined
                              @keypress="onlyLetters"
                              :rules="[
                                (v) =>
                                  !v ||
                                  v.length <= 50 ||
                                  'Middle name must be less than 50 characters',
                                (v) =>
                                  !v ||
                                  /^[a-zA-Z\s]+$/.test(v) ||
                                  'Middle name can only contain letters and spaces',
                              ]"
                              :error-messages="errors.middle_name"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="basicForm.last_name"
                              label="Last Name *"
                              placeholder="Enter last name"
                              outlined
                              @keypress="onlyLetters"
                              :rules="[
                                (v) => !!v || 'Last name is required',
                                (v) =>
                                  (v && v.length >= 2) ||
                                  'Last name must be at least 2 characters',
                                (v) =>
                                  (v && v.length <= 50) ||
                                  'Last name must be less than 50 characters',
                                (v) =>
                                  (v && /^[a-zA-Z\s]+$/.test(v)) ||
                                  'Last name can only contain letters and spaces',
                              ]"
                              :error-messages="errors.last_name"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="basicForm.email"
                              label="Email Address *"
                              placeholder="Enter email address"
                              outlined
                              type="email"
                              @input="errors.email = null"
                              :rules="[
                                (v) => !!v || 'Email is required',
                                (v) =>
                                  /.+@.+\..+/.test(v) || 'Email must be valid',
                                (v) =>
                                  (v && v.length <= 100) ||
                                  'Email must be less than 100 characters',
                              ]"
                              :error-messages="errors.email"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-email</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="basicForm.phone"
                              label="Phone Number *"
                              placeholder="Enter phone number"
                              outlined
                              maxlength="10"
                              @keypress="onlyNumbers"
                              @input="errors.phone = null"
                              :rules="[
                                (v) => !!v || 'Phone number is required',
                                (v) =>
                                  (v && /^\d{10}$/.test(v)) ||
                                  'Phone number must be exactly 10 digits',
                              ]"
                              :error-messages="errors.phone"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-phone</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="basicForm.gender_id"
                              :items="genderItems"
                              label="Gender *"
                              placeholder="Select gender"
                              outlined
                              :rules="[(v) => !!v || 'Gender is required']"
                              :error-messages="errors.gender_id"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey"
                                  >mdi-human-male-female</v-icon
                                >
                              </template>
                            </v-select>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-menu
                              v-model="dobPickerMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                              max-width="290px"
                              min-width="auto"
                            >
                              <template #activator="{ on, attrs }">
                                <v-text-field
                                  :value="formattedDOB"
                                  label="Date of Birth *"
                                  prepend-inner-icon="mdi-calendar"
                                  outlined
                                  readonly
                                  v-bind="attrs"
                                  v-on="on"
                                  :rules="[
                                    (v) =>
                                      !!basicForm.dob ||
                                      'Date of birth is required',
                                  ]"
                                  :error-messages="errors.dob"
                                  class="professional-field"
                                  hide-details="auto"
                                  clearable
                                  @click:clear="clearDOB"
                                  placeholder="Select date of birth"
                                  required
                                />
                              </template>
                              <v-date-picker
                                v-model="basicForm.dob"
                                no-title
                                scrollable
                                color="primary"
                                :max="maxDate"
                                :min="minDate"
                                @input="dobPickerMenu = false"
                              />
                            </v-menu>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="basicForm.password"
                              label="Password *"
                              placeholder="Enter password"
                              outlined
                              type="password"
                              :rules="[
                                (v) => !!v || 'Password is required',
                                (v) =>
                                  (v && v.length >= 6) ||
                                  'Password must be at least 6 characters',
                                (v) =>
                                  (v && v.length <= 50) ||
                                  'Password must be less than 50 characters',
                              ]"
                              :error-messages="errors.password"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-lock</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="basicForm.password_confirmation"
                              label="Confirm Password *"
                              placeholder="Confirm password"
                              outlined
                              type="password"
                              :rules="[
                                (v) =>
                                  !!v || 'Password confirmation is required',
                                (v) =>
                                  v === basicForm.password ||
                                  'Passwords do not match',
                              ]"
                              :error-messages="errors.password_confirmation"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-lock-check</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <v-textarea
                              v-model="basicForm.address"
                              label="Address (Optional)"
                              placeholder="Enter complete address"
                              outlined
                              rows="3"
                              :error-messages="errors.address"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-map-marker</v-icon>
                              </template>
                            </v-textarea>
                          </v-col>
                        </v-row>
                      </div>
                    </v-form>
                  </v-stepper-content>

                  <!-- Step 2: Education Details -->
                  <v-stepper-content step="2">
                    <v-form
                      ref="educationForm"
                      v-model="educationFormValid"
                      lazy-validation
                    >
                      <div class="form-section">
                        <div
                          class="d-flex justify-space-between align-center mb-4"
                        >
                          <h3 class="section-title mb-0">
                            <v-icon color="primary" class="mr-2"
                              >mdi-school</v-icon
                            >
                            Education & Qualifications
                          </h3>
                          <v-btn
                            color="primary"
                            outlined
                            small
                            @click="addEducationEntry"
                            :disabled="educationForm.educations.length >= 5"
                          >
                            <v-icon left small>mdi-plus</v-icon>
                            Add Education
                          </v-btn>
                        </div>

                        <!-- Education Entries -->
                        <div
                          v-for="(education, index) in educationForm.educations"
                          :key="index"
                          class="education-entry mb-4"
                        >
                          <!-- Entry Header -->
                          <div
                            class="d-flex justify-space-between align-center mb-3"
                          >
                            <h4 class="entry-title">
                              <v-icon color="primary" small class="mr-2"
                                >mdi-certificate</v-icon
                              >
                              Education {{ index + 1 }}
                              {{
                                education.qualification
                                  ? ` - ${education.qualification}`
                                  : ""
                              }}
                            </h4>
                            <v-btn
                              v-if="educationForm.educations.length > 1"
                              color="error"
                              icon
                              small
                              @click="removeEducationEntry(index)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>

                          <!-- Education Fields -->
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="education.qualification"
                                :label="`Qualification ${index + 1} *`"
                                placeholder="e.g., MBBS, MD, BAMS"
                                outlined
                                :rules="[
                                  (v) => !!v || 'Qualification is required',
                                  (v) =>
                                    (v && v.length >= 2) ||
                                    'Qualification must be at least 2 characters',
                                  (v) =>
                                    (v && v.length <= 100) ||
                                    'Qualification must be less than 100 characters',
                                ]"
                                :error-messages="
                                  getFieldError('qualification', index)
                                "
                                class="professional-field"
                                hide-details="auto"
                                required
                              >
                                <template #prepend-inner>
                                  <v-icon color="grey">mdi-certificate</v-icon>
                                </template>
                              </v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="education.specialization"
                                :label="`Specialization ${index + 1}`"
                                placeholder="e.g., Cardiology, Ayurveda"
                                outlined
                                :rules="[
                                  (v) =>
                                    !v ||
                                    v.length <= 100 ||
                                    'Specialization must be less than 100 characters',
                                ]"
                                :error-messages="
                                  getFieldError('specialization', index)
                                "
                                class="professional-field"
                                hide-details="auto"
                              >
                                <template #prepend-inner>
                                  <v-icon color="grey">mdi-medical-bag</v-icon>
                                </template>
                              </v-text-field>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="education.university"
                                :label="`University/College ${index + 1} *`"
                                placeholder="Name of institution"
                                outlined
                                :rules="[
                                  (v) =>
                                    !!v || 'University/College is required',
                                  (v) =>
                                    (v && v.length >= 2) ||
                                    'University name must be at least 2 characters',
                                  (v) =>
                                    (v && v.length <= 200) ||
                                    'University name must be less than 200 characters',
                                ]"
                                :error-messages="
                                  getFieldError('university', index)
                                "
                                class="professional-field"
                                hide-details="auto"
                                required
                              >
                                <template #prepend-inner>
                                  <v-icon color="grey"
                                    >mdi-school-outline</v-icon
                                  >
                                </template>
                              </v-text-field>
                            </v-col>

                            <v-col cols="12" md="6">
                              <v-text-field
                                v-model="education.year_of_passing"
                                :label="`Year of Passing ${index + 1} *`"
                                placeholder="YYYY"
                                type="number"
                                min="1950"
                                :max="new Date().getFullYear()"
                                outlined
                                :rules="[
                                  (v) => !!v || 'Year of passing is required',
                                  (v) =>
                                    (v && v >= 1950) ||
                                    'Year must be 1950 or later',
                                  (v) =>
                                    (v && v <= new Date().getFullYear()) ||
                                    'Year cannot be in the future',
                                  (v) =>
                                    (v && String(v).length === 4) ||
                                    'Year must be 4 digits',
                                ]"
                                :error-messages="
                                  getFieldError('year_of_passing', index)
                                "
                                class="professional-field"
                                hide-details="auto"
                                required
                              >
                                <template #prepend-inner>
                                  <v-icon color="grey"
                                    >mdi-calendar-clock</v-icon
                                  >
                                </template>
                              </v-text-field>
                            </v-col>
                          </v-row>

                          <v-row>
                            <v-col cols="12">
                              <v-file-input
                                v-model="education.certificate_file"
                                :label="`Education Certificate ${index + 1} *`"
                                placeholder="Upload degree certificate"
                                outlined
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                prepend-icon=""
                                :rules="[
                                  (v) => !!v || 'Certificate file is required',
                                ]"
                                :error-messages="
                                  getFieldError('certificate_file', index)
                                "
                                class="professional-field"
                                hide-details="auto"
                                required
                              >
                                <template #prepend-inner>
                                  <v-icon color="grey"
                                    >mdi-file-document-outline</v-icon
                                  >
                                </template>
                              </v-file-input>
                            </v-col>
                          </v-row>

                          <!-- Divider between entries -->
                          <v-divider
                            v-if="index < educationForm.educations.length - 1"
                            class="mt-4"
                          ></v-divider>
                        </div>

                        <!-- Summary Card -->
                        <v-card
                          v-if="educationForm.educations.length > 1"
                          outlined
                          color="info"
                          class="pa-3 mt-4"
                        >
                          <div class="d-flex align-center">
                            <v-icon color="info" class="mr-2"
                              >mdi-information</v-icon
                            >
                            <div>
                              <div class="font-weight-medium">
                                {{ educationForm.educations.length }} education
                                {{
                                  educationForm.educations.length > 1
                                    ? "qualifications"
                                    : "qualification"
                                }}
                                added
                              </div>
                              <div class="text-caption grey--text">
                                You can add up to 5 education qualifications
                              </div>
                            </div>
                          </div>
                        </v-card>
                      </div>
                    </v-form>
                  </v-stepper-content>

                  <!-- Step 3: Council Registration -->
                  <v-stepper-content step="3">
                    <v-form
                      ref="councilForm"
                      v-model="councilFormValid"
                      lazy-validation
                    >
                      <div class="form-section">
                        <h3 class="section-title mb-4">
                          <v-icon color="primary" class="mr-2"
                            >mdi-card-account-details</v-icon
                          >
                          Medical Council Registration
                        </h3>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="councilForm.council_name"
                              label="Medical Council Name *"
                              placeholder="e.g., Medical Council of India"
                              outlined
                              :rules="[
                                (v) => !!v || 'Council name is required',
                                (v) =>
                                  (v && v.length >= 3) ||
                                  'Council name must be at least 3 characters',
                                (v) =>
                                  (v && v.length <= 100) ||
                                  'Council name must be less than 100 characters',
                              ]"
                              :error-messages="errors.council_name"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account-group</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="councilForm.registration_number"
                              label="Registration Number *"
                              placeholder="Enter license number"
                              outlined
                              :rules="[
                                (v) => !!v || 'Registration number is required',
                                (v) =>
                                  (v && v.length >= 3) ||
                                  'Registration number must be at least 3 characters',
                                (v) =>
                                  (v && v.length <= 50) ||
                                  'Registration number must be less than 50 characters',
                                (v) =>
                                  (v && /^[A-Za-z0-9\/\-]+$/.test(v)) ||
                                  'Registration number can only contain letters, numbers, hyphens and slashes',
                              ]"
                              :error-messages="errors.registration_number"
                              class="professional-field"
                              hide-details="auto"
                              required
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-identifier</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="4">
                            <v-text-field
                              v-model="councilForm.registration_year"
                              label="Registration Year"
                              placeholder="YYYY"
                              type="number"
                              min="1950"
                              :max="new Date().getFullYear()"
                              outlined
                              :error-messages="errors.registration_year"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-calendar</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="4">
                            <v-menu
                              v-model="expiryDateMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y min-width="auto"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="councilForm.expiry_date"
                                  label="Expiry Date"
                                  prepend-inner-icon="mdi-calendar-remove"
                                  readonly outlined clearable
                                  :error-messages="errors.expiry_date"
                                  class="professional-field"
                                  hide-details="auto"
                                  v-bind="attrs" v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="councilForm.expiry_date"
                                no-title scrollable
                                @input="expiryDateMenu = false"
                              ></v-date-picker>
                            </v-menu>
                          </v-col>

                          <v-col cols="12" md="4">
                            <v-menu
                              v-model="renewalDateMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y min-width="auto"
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="councilForm.renewal_date"
                                  label="Last Renewal Date"
                                  prepend-inner-icon="mdi-calendar-refresh"
                                  readonly outlined clearable
                                  :error-messages="errors.renewal_date"
                                  class="professional-field"
                                  hide-details="auto"
                                  v-bind="attrs" v-on="on"
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="councilForm.renewal_date"
                                no-title scrollable
                                @input="renewalDateMenu = false"
                              ></v-date-picker>
                            </v-menu>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <v-file-input
                              v-model="councilForm.certificate_file"
                              label="Registration Certificate"
                              placeholder="Upload medical license certificate"
                              outlined
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              prepend-icon=""
                              :error-messages="errors.certificate_path"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey"
                                  >mdi-file-certificate-outline</v-icon
                                >
                              </template>
                            </v-file-input>
                          </v-col>
                        </v-row>
                      </div>
                    </v-form>
                  </v-stepper-content>

                  <!-- Step 4: Doctor Specializations -->
                  <v-stepper-content step="4">
                    <v-form
                      ref="specializationForm"
                      v-model="specializationFormValid"
                      lazy-validation
                    >
                      <div class="form-section">
                        <h3 class="section-title mb-4">
                          <v-icon color="primary" class="mr-2"
                            >mdi-medical-bag</v-icon
                          >
                          Doctor Specializations
                        </h3>

                        <v-row>
                          <v-col cols="12">
                            <v-select
                              v-model="specializationForm.specializations"
                              :items="specializationItems"
                              label="Medical Specializations"
                              placeholder="Select your areas of medical expertise"
                              outlined
                              multiple
                              chips
                              deletable-chips
                              :error-messages="errors.specializations"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-medical-bag</v-icon>
                              </template>
                              <template #selection="{ item, index }">
                                <v-chip
                                  v-if="index < 3"
                                  color="primary"
                                  outlined
                                  small
                                  close
                                  @click:close="
                                    removeSpecialization(item.value)
                                  "
                                >
                                  {{ item.text }}
                                </v-chip>
                                <span
                                  v-if="index === 3"
                                  class="text-caption grey--text"
                                >
                                  (+{{
                                    specializationForm.specializations.length -
                                    3
                                  }}
                                  others)
                                </span>
                              </template>
                            </v-select>
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="specializationForm.specializations.length > 0"
                        >
                          <v-col cols="12">
                            <v-card color="success" class="pa-3">
                              <div class="d-flex align-center">
                                <v-icon color="white" class="mr-2"
                                  >mdi-check-circle</v-icon
                                >
                                <div>
                                  <div class="font-weight-medium white--text">
                                    {{
                                      specializationForm.specializations.length
                                    }}
                                    specialization{{
                                      specializationForm.specializations
                                        .length > 1
                                        ? "s"
                                        : ""
                                    }}
                                    selected
                                  </div>
                                  <div class="text-caption white--text">
                                    These specializations will be associated
                                    with the doctor's profile
                                  </div>
                                </div>
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="specializationForm.specializations.length === 0"
                        >
                          <v-col cols="12">
                            <v-card color="info" class="pa-3">
                              <div class="d-flex align-center">
                                <v-icon color="white" class="mr-2"
                                  >mdi-information</v-icon
                                >
                                <div>
                                  <div class="font-weight-medium white--text">
                                    No specializations selected
                                  </div>
                                  <div class="text-caption white--text">
                                    You can skip this step and add
                                    specializations later from the doctor's
                                    profile
                                  </div>
                                </div>
                              </div>
                            </v-card>
                          </v-col>
                        </v-row>
                      </div>
                    </v-form>
                  </v-stepper-content>

                  <!-- Step 5: Documents - Hidden for now -->
                  <!-- <v-stepper-content step="5">
                    <v-form ref="documentsForm" v-model="documentsFormValid" lazy-validation>
                      <div class="form-section">
                        <h3 class="section-title mb-4">
                          <v-icon color="primary" class="mr-2">mdi-file-multiple</v-icon>
                          Additional Documents
                        </h3>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-file-input
                              v-model="documentsForm.id_proof"
                              label="ID Proof (Optional)"
                              placeholder="Upload ID proof (Aadhar, PAN, etc.)"
                              outlined
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              prepend-icon=""
                              :error-messages="errors.id_proof"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-card-account-details-outline</v-icon>
                              </template>
                            </v-file-input>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-file-input
                              v-model="documentsForm.address_proof"
                              label="Address Proof (Optional)"
                              placeholder="Upload address proof"
                              outlined
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              prepend-icon=""
                              :error-messages="errors.address_proof"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-home-account</v-icon>
                              </template>
                            </v-file-input>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <v-file-input
                              v-model="documentsForm.profile_photo"
                              label="Profile Photo (Optional)"
                              placeholder="Upload profile photo"
                              outlined
                              accept=".jpg,.jpeg,.png"
                              prepend-icon=""
                              :error-messages="errors.profile_photo"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-camera-account</v-icon>
                              </template>
                            </v-file-input>
                          </v-col>
                        </v-row>
                      </div>
                    </v-form>
                  </v-stepper-content> -->

                  <!-- Step 5: Bank Details (was Step 6) -->
                  <v-stepper-content step="5">
                    <v-form
                      ref="bankForm"
                      v-model="bankFormValid"
                      lazy-validation
                    >
                      <div class="form-section">
                        <h3 class="section-title mb-4">
                          <v-icon color="primary" class="mr-2">mdi-bank</v-icon>
                          Banking Information
                        </h3>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.account_holder_name"
                              label="Account Holder Name"
                              placeholder="Enter account holder name"
                              outlined
                              :rules="[
                                (v) =>
                                  !v ||
                                  v.length >= 2 ||
                                  'Account holder name must be at least 2 characters',
                                (v) =>
                                  !v ||
                                  v.length <= 100 ||
                                  'Account holder name must be less than 100 characters',
                                (v) =>
                                  !v ||
                                  /^[a-zA-Z\s.]+$/.test(v) ||
                                  'Account holder name can only contain letters, spaces and dots',
                              ]"
                              :error-messages="errors.account_holder_name"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-account</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.account_number"
                              label="Account Number"
                              placeholder="Enter bank account number"
                              outlined
                              :rules="[
                                (v) =>
                                  !v ||
                                  /^[0-9]{9,18}$/.test(v) ||
                                  'Account number must be 9-18 digits',
                                (v) =>
                                  !v ||
                                  v.length >= 9 ||
                                  'Account number must be at least 9 digits',
                              ]"
                              :error-messages="errors.account_number"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey"
                                  >mdi-credit-card-outline</v-icon
                                >
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.ifsc_code"
                              label="IFSC Code"
                              placeholder="Enter IFSC code"
                              outlined
                              :rules="[
                                (v) =>
                                  !v ||
                                  /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v) ||
                                  'IFSC code must be in format: ABCD0123456 (4 letters, one 0, 6 alphanumeric)',
                              ]"
                              :error-messages="errors.ifsc_code"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-bank-transfer</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>

                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="bankForm.bank_name"
                              label="Bank Name"
                              placeholder="Enter bank name"
                              outlined
                              :rules="[
                                (v) =>
                                  !v ||
                                  v.length >= 2 ||
                                  'Bank name must be at least 2 characters',
                                (v) =>
                                  !v ||
                                  v.length <= 100 ||
                                  'Bank name must be less than 100 characters',
                              ]"
                              :error-messages="errors.bank_name"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-bank</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12">
                            <v-text-field
                              v-model="bankForm.branch_name"
                              label="Branch Name"
                              placeholder="Enter branch name"
                              outlined
                              :rules="[
                                (v) =>
                                  !v ||
                                  v.length >= 2 ||
                                  'Branch name must be at least 2 characters',
                                (v) =>
                                  !v ||
                                  v.length <= 100 ||
                                  'Branch name must be less than 100 characters',
                              ]"
                              :error-messages="errors.branch_name"
                              class="professional-field"
                              hide-details="auto"
                            >
                              <template #prepend-inner>
                                <v-icon color="grey">mdi-source-branch</v-icon>
                              </template>
                            </v-text-field>
                          </v-col>
                        </v-row>
                      </div>
                    </v-form>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Form Actions -->
            <v-card-actions class="form-actions py-3 px-4">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Form Status -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-chip
                      :color="getCurrentFormValid() ? 'success' : 'warning'"
                      outlined
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>{{
                        getCurrentFormValid()
                          ? "mdi-check-circle"
                          : "mdi-alert-circle"
                      }}</v-icon>
                      {{
                        getCurrentFormValid() ? "Step Valid" : "Step Invalid"
                      }}
                    </v-chip>
                    <span class="text-caption grey--text">
                      {{
                        getCurrentFormValid()
                          ? "Ready to proceed"
                          : "Please complete required fields"
                      }}
                    </span>
                  </div>
                </v-col>

                <!-- Right Side: Action Buttons -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-btn
                      v-if="currentStep > 1"
                      outlined
                      color="grey darken-1"
                      @click="previousStep"
                      class="mr-2"
                      :disabled="loading"
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Previous
                    </v-btn>
                    <v-btn
                      v-if="currentStep < totalSteps"
                      color="primary"
                      @click="nextStep"
                      :loading="loading"
                      :disabled="loading"
                      class="next-button"
                    >
                      <v-icon left>mdi-arrow-right</v-icon>
                      Next Step
                    </v-btn>
                    <v-btn
                      v-if="currentStep === totalSteps"
                      color="success"
                      @click="completeDoctorRegistration"
                      :loading="loading"
                      :disabled="loading"
                      class="submit-button"
                    >
                      <v-icon left>mdi-check</v-icon>
                      Complete Registration
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "DoctorCreatePage",
  data() {
    return {
      currentStep: 1,
      totalSteps: 5,
      loading: false,
      errors: {},

      // Date picker
      dobPickerMenu: false,

      // Form validation states
      basicFormValid: false,
      educationFormValid: false,
      councilFormValid: false,
      specializationFormValid: true, // Optional but recommended step
      documentsFormValid: true, // Optional step
      bankFormValid: true, // Optional step

      // Created record IDs for step progression
      createdUserId: null,
      createdEducationIds: [],
      createdCouncilId: null,
      createdSpecializationIds: [],

      // Step 1: Basic Details
      basicForm: {
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone: "",
        gender_id: null,
        dob: "",
        password: "",
        password_confirmation: "",
        address: "",
        is_active: true,
      },

      // Step 2: Education Details
      educationForm: {
        educations: [
          {
            qualification: "",
            university: "",
            specialization: "",
            year_of_passing: null,
            certificate_file: null,
          },
        ],
      },

      // Date picker menu state for council step
      expiryDateMenu: false,
      renewalDateMenu: false,

      // Step 3: Council Registration
      councilForm: {
        council_name: "",
        registration_number: "",
        registration_year: null,
        expiry_date: "",
        renewal_date: "",
        certificate_file: null,
      },

      // Step 4: Doctor Specializations
      specializationForm: {
        specializations: [], // Array of selected specialization IDs
      },

      // Step 5: Documents
      documentsForm: {
        id_proof: null,
        address_proof: null,
        profile_photo: null,
      },

      // Step 6: Bank Details
      bankForm: {
        account_holder_name: "",
        account_number: "",
        ifsc_code: "",
        bank_name: "",
        branch_name: "",
      },

      // Data lists
      genderItems: [], // Will be populated from API
      specializationItems: [], // Will be populated from API
    };
  },

  computed: {
    formattedDOB() {
      if (!this.basicForm.dob) return "";

      const date = new Date(this.basicForm.dob);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },

    maxDate() {
      // Maximum age allowed is 18 years old (current date minus 18 years)
      const today = new Date();
      today.setFullYear(today.getFullYear() - 18);
      return today.toISOString().substr(0, 10);
    },

    minDate() {
      // Minimum age allowed is 100 years old (current date minus 100 years)
      const today = new Date();
      today.setFullYear(today.getFullYear() - 100);
      return today.toISOString().substr(0, 10);
    },
  },

  mounted() {
    this.getMasters();
  },

  methods: {
    async getMasters() {
      try {
        const response = await axios.get(
          `/masters?context=doctor_specialities`
        );

        // Populate genders dropdown
        if (response.data.genders) {
          this.genderItems = response.data.genders.map((gender) => ({
            text: gender.description,
            value: gender.id,
          }));
        }

        // Populate specializations dropdown
        if (response.data.specializations) {
          this.specializationItems = response.data.specializations.map(
            (spec) => ({
              text: spec.description,
              value: spec.id,
            })
          );
        }
      } catch (error) {
        console.error("Error fetching masters:", error);
        this.$toast.error("Failed to load form data. Please try again.");
      }
    },

    getCurrentStepDescription() {
      const descriptions = {
        1: "Enter basic personal and contact information",
        2: "Provide education qualifications and certificates",
        3: "Add medical council registration details",
        4: "Select medical specializations and expertise areas",
        5: "Complete with banking information",
      };
      return descriptions[this.currentStep];
    },

    getCurrentFormValid() {
      const validations = {
        1: this.basicFormValid,
        2: this.educationFormValid,
        3: this.councilFormValid,
        4: this.specializationFormValid,
        5: this.bankFormValid,
      };
      return validations[this.currentStep];
    },

    getCurrentFormRef() {
      const formRefs = {
        1: this.$refs.basicForm,
        2: this.$refs.educationForm,
        3: this.$refs.councilForm,
        4: this.$refs.specializationForm,
        5: this.$refs.bankForm,
      };
      return formRefs[this.currentStep];
    },

    handleBackNavigation() {
      if (this.currentStep === 1) {
        this.$router.push("/doctors");
      } else {
        this.previousStep();
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.errors = {};
      }
    },

    async nextStep() {
      // Validate current step form
      const currentFormRef = this.getCurrentFormRef();
      if (!currentFormRef || !currentFormRef.validate()) {
        this.$toast.error("Please complete all required fields in this step.");
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        console.log(`Processing step ${this.currentStep}...`);

        // Save current step data
        switch (this.currentStep) {
          case 1:
            await this.saveBasicDetails();
            break;
          case 2:
            await this.saveEducationDetails();
            break;
          case 3:
            await this.saveCouncilRegistration();
            break;
          case 4:
            await this.saveSpecializations();
            break;
        }

        // Update registration_step on the backend after each step completes
        if (this.createdUserId) {
          const nextStepNumber = this.currentStep + 1;
          await axios.patch(`/users/${this.encryptIt(String(this.createdUserId))}`, {
            registration_step: nextStepNumber,
          }).catch(() => {}); // Non-blocking — don't fail the whole flow
        }

        this.currentStep++;
      } catch (error) {
        console.error(`Error in step ${this.currentStep}:`, error);
        this.handleStepError(error);
      } finally {
        this.loading = false;
      }
    },

    async saveBasicDetails() {
      console.log("Starting saveBasicDetails...");

      // Find DOCTOR position
      const doctorPosition = await this.getDoctorPosition();
      console.log("Doctor position ID:", doctorPosition);

      const formData = {
        ...this.basicForm,
        position_id: doctorPosition,
        soft_password: this.basicForm.password,
        registration_step: 1,
      };

      console.log("Sending form data:", formData);
      const response = await axios.post("/users", formData);
      console.log("API response:", response.data);

      // Handle API response structure: response.data.data contains the user object
      let userId = null;
      if (response.data && response.data.data && response.data.data.id) {
        userId = response.data.data.id;
      } else if (response.data && response.data.user && response.data.user.id) {
        userId = response.data.user.id;
      } else if (response.data && response.data.id) {
        userId = response.data.id;
      } else {
        console.error("Unexpected API response structure:", response.data);
        throw new Error("User ID not found in API response");
      }

      this.createdUserId = userId;
      console.log("Created user ID:", this.createdUserId);

      this.$toast.success("Basic details saved successfully!");
      console.log("saveBasicDetails completed successfully");
    },

    async saveEducationDetails() {
      if (!this.createdUserId) {
        throw new Error("User ID not found. Please start from step 1.");
      }

      const formData = new FormData();
      formData.append("user_id", this.createdUserId);

      // Add education details and files in the expected format
      this.educationForm.educations.forEach((edu, index) => {
        formData.append(
          `education_details[${index}][qualification]`,
          edu.qualification || ""
        );
        formData.append(
          `education_details[${index}][university]`,
          edu.university || ""
        );
        formData.append(
          `education_details[${index}][specialization]`,
          edu.specialization || ""
        );
        formData.append(
          `education_details[${index}][year_of_passing]`,
          edu.year_of_passing || ""
        );

        // Add certificate file directly as part of education_details structure
        if (edu.certificate_file) {
          formData.append(
            `education_details[${index}][certificate_file]`,
            edu.certificate_file
          );
        }
      });

      const response = await axios.post(
        "/education_details/multiple",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Handle API response structure
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        this.createdEducationIds = response.data.data.map((edu) => edu.id);
        const count = response.data.data.length;
        this.$toast.success(
          `${count} education detail${count > 1 ? "s" : ""} saved successfully!`
        );
      } else {
        this.$toast.success("Education details saved successfully!");
      }
    },

    async saveCouncilRegistration() {
      if (!this.createdUserId) {
        throw new Error("User ID not found. Please start from step 1.");
      }

      const formData = new FormData();
      formData.append("user_id", this.createdUserId);
      formData.append("council_name", this.councilForm.council_name);
      formData.append(
        "registration_number",
        this.councilForm.registration_number
      );

      if (this.councilForm.registration_year) {
        formData.append(
          "registration_year",
          this.councilForm.registration_year
        );
      }
      if (this.councilForm.expiry_date) {
        formData.append("expiry_date", this.councilForm.expiry_date);
      }
      if (this.councilForm.renewal_date) {
        formData.append("renewal_date", this.councilForm.renewal_date);
      }
      if (this.councilForm.certificate_file) {
        formData.append("certificate_path", this.councilForm.certificate_file);
      }

      const response = await axios.post(
        "/council_registration_certificates",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Handle API response structure: response.data.data contains the council certificate object
      this.createdCouncilId = response.data.data.id;
      this.$toast.success("Council registration saved successfully!");
    },

    async saveSpecializations() {
      if (!this.createdUserId) {
        throw new Error("User ID not found. Please start from step 1.");
      }

      // Save each selected specialization separately
      const savedSpecializations = [];

      for (const specializationId of this.specializationForm.specializations) {
        const formData = {
          doctor_id: this.createdUserId,
          specialization_id: specializationId,
        };

        const response = await axios.post("/doctor_specialities", formData);
        // Handle API response structure: response.data.data contains the doctor speciality object
        savedSpecializations.push(response.data.data.id);
      }

      this.createdSpecializationIds = savedSpecializations;

      if (this.specializationForm.specializations.length > 0) {
        this.$toast.success(
          `${this.specializationForm.specializations.length} specialization(s) saved successfully!`
        );
      } else {
        this.$toast.success("Specializations step completed!");
      }
    },

    async saveDocuments() {
      if (!this.createdUserId) {
        throw new Error("User ID not found. Please start from step 1.");
      }

      // Save each document separately if they exist
      const documents = [
        { file: this.documentsForm.id_proof, type: "ID_PROOF" },
        { file: this.documentsForm.address_proof, type: "ADDRESS_PROOF" },
        { file: this.documentsForm.profile_photo, type: "PROFILE_PHOTO" },
      ];

      for (const doc of documents) {
        if (doc.file) {
          const formData = new FormData();
          formData.append("user_id", this.createdUserId);
          formData.append("doc_type_id", doc.type); // You may need to get actual doc type IDs
          formData.append("file_path", doc.file);

          await axios.post("/user_documents", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }
      }

      this.$toast.success("Documents uploaded successfully!");
    },

    async completeDoctorRegistration() {
      // Validate current step form
      console.log("Step 5: 1");
      const currentFormRef = this.getCurrentFormRef();
      if (currentFormRef && !currentFormRef.validate()) {
        this.$toast.error("Please complete all required fields in this step.");
        return;
      }

      console.log("Step 5: 2");
      this.loading = true;
      this.errors = {};

      try {
        // Save bank details if provided
        console.log("Step 5: 3");
        await this.saveBankDetails();
        console.log("Step 5: 4");

        this.$toast.success("Doctor registration completed successfully!");

        // Redirect to doctors index page
        console.log("Step 5: 5");
        this.$router.push("/doctors");
      } catch (error) {
        console.log("Step 5: 6");
        this.handleStepError(error);
      } finally {
        console.log("Step 5: 7");
        this.loading = false;
      }
    },

    async saveBankDetails() {
      if (!this.createdUserId) {
        throw new Error("User ID not found. Please start from step 1.");
      }

      const formData = {
        user_id: this.createdUserId,
        account_name: this.bankForm.account_holder_name,
        account_number: this.bankForm.account_number,
        ifsc_code: this.bankForm.ifsc_code,
        bank_name: this.bankForm.bank_name,
        branch: this.bankForm.branch_name,
      };

      await axios.post("/bank_details", formData);
      this.$toast.success("Bank details saved successfully!");
    },

    hasBankDetails() {
      return (
        this.bankForm.account_holder_name ||
        this.bankForm.account_number ||
        this.bankForm.ifsc_code ||
        this.bankForm.bank_name ||
        this.bankForm.branch_name
      );
    },

    async getDoctorPosition() {
      try {
        const response = await axios.get("/masters?context=user");
        if (
          response.data &&
          response.data.positions &&
          Array.isArray(response.data.positions)
        ) {
          const doctorPosition = response.data.positions.find(
            (position) =>
              position.name && position.name.toUpperCase() === "DOCTOR"
          );
          return doctorPosition ? doctorPosition.id : 1; // Default to position ID 1 if not found
        }
        return 1; // Default position ID
      } catch (error) {
        console.error("Error fetching doctor position:", error);
        console.warn("Using default position ID 1 for doctor");
        return 1; // Default position ID
      }
    },

    removeSpecialization(specializationId) {
      const index =
        this.specializationForm.specializations.indexOf(specializationId);
      if (index > -1) {
        this.specializationForm.specializations.splice(index, 1);
      }
    },

    addEducationEntry() {
      if (this.educationForm.educations.length < 5) {
        this.educationForm.educations.push({
          qualification: "",
          university: "",
          specialization: "",
          year_of_passing: null,
          certificate_file: null,
        });
      }
    },

    removeEducationEntry(index) {
      if (this.educationForm.educations.length > 1) {
        this.educationForm.educations.splice(index, 1);
      }
    },

    getFieldError(fieldName, index) {
      // Helper method to get field-specific errors from API validation
      if (
        this.errors &&
        this.errors[`education_details.${index}.${fieldName}`]
      ) {
        return this.errors[`education_details.${index}.${fieldName}`];
      }
      if (this.errors && this.errors[`educations.${index}.${fieldName}`]) {
        return this.errors[`educations.${index}.${fieldName}`];
      }
      if (this.errors && this.errors[fieldName]) {
        return this.errors[fieldName];
      }
      return [];
    },

    clearDOB() {
      this.basicForm.dob = "";
      this.dobPickerMenu = false;
    },

    onlyNumbers(e) {
      if (!/[0-9]/.test(e.key)) e.preventDefault();
    },

    onlyLetters(e) {
      if (!/[a-zA-Z\s]/.test(e.key)) e.preventDefault();
    },

    handleStepError(error) {
      if (error.response && error.response.status === 422) {
        this.errors = error.response.data.errors || {};
        this.$toast.error("Please check the form for validation errors.");
      } else {
        this.$toast.error("An error occurred. Please try again.");
        console.error("Step error:", error);
      }
    },
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.page-header {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-bottom: 1px solid #e0e0e0;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.page-subtitle {
  font-size: 0.875rem;
  opacity: 0.8;
}

.back-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.professional-field >>> .v-input__control {
  border-radius: 8px !important;
}

.professional-field >>> .v-text-field__details {
  margin-top: 8px;
}

.form-actions {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-top: 1px solid #e0e0e0;
}

.next-button,
.submit-button {
  border-radius: 8px;
  text-transform: none;
  font-weight: 600;
  padding: 8px 24px;
}

.v-chip {
  font-weight: 500;
}

.v-stepper {
  box-shadow: none !important;
}

.v-stepper__header {
  border-radius: 12px;
  background: #fafafa;
  box-shadow: none !important;
}

.v-stepper__step {
  padding: 16px;
}

.v-stepper__content {
  padding: 24px 0 16px 0;
}

.education-entry {
  background: #fafafa;
  border-radius: 12px;
  padding: 20px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.education-entry:hover {
  border-color: var(--v-primary-base);
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
}

.entry-title {
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
  display: flex;
  align-items: center;
}
</style>